import { ReactLenis } from "lenis/react";
import React, { Fragment, useEffect, useState, type ReactNode } from "react";
import "lenis/dist/lenis.css";

/** Lenis options — desktop only (wheel / trackpad). Touch devices use native scroll. */
const lenisOptions = {
  autoRaf: true,
  lerp: 0.09,
  smoothWheel: true,
  syncTouch: false,
  wheelMultiplier: 0.9,
  anchors: true,
} as const;

function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Smooth, momentum-style scrolling (Lenis). Disabled on touch/mobile and when
 * the user prefers reduced motion — both cases fall back to native browser scroll.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  const [disabled, setDisabled] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia("(prefers-reduced-motion: reduce)").matches || isTouchDevice()),
  );

  useEffect(() => {
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointerMq = window.matchMedia("(pointer: coarse)");
    const update = () => setDisabled(motionMq.matches || pointerMq.matches);
    update();
    motionMq.addEventListener("change", update);
    pointerMq.addEventListener("change", update);
    return () => {
      motionMq.removeEventListener("change", update);
      pointerMq.removeEventListener("change", update);
    };
  }, []);

  if (disabled) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <ReactLenis root options={lenisOptions}>
      {children}
    </ReactLenis>
  );
}
